import React, { useEffect, useRef, useState } from 'react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import {
  Loader2, X, AlertCircle, Clock, Search, MapPin, Calendar as CalendarIcon,
  Paperclip, CheckCircle2, ChevronLeft, Upload,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const REASONS = [
  ['forgot_clock_in', 'Missing clock-in (I forgot / couldn’t clock in)'],
  ['clock_out_failed', 'Missing clock-out'],
  ['hours_missing', 'Incorrect or missing hours'],
  ['missing_timesheet', 'Missing timesheet'],
  ['missing_settlement', 'Missing settlement'],
  ['missing_payment', 'Missing payment'],
  ['incorrect_payment', 'Incorrect payment amount'],
  ['not_on_roster', 'I worked but was never added to the roster'],
];

const PAYOUT_TONE = {
  'Pending Review': 'bg-amber-100 text-amber-800',
  'Awaiting Additional Information': 'bg-orange-100 text-orange-800',
  'Awaiting Scheduled Payment': 'bg-indigo-100 text-indigo-800',
  'Approved': 'bg-emerald-100 text-emerald-800',
  'Payment Processing': 'bg-blue-100 text-blue-800',
  'Paid': 'bg-emerald-600 text-white',
  'Reviewed — Not Approved': 'bg-gray-200 text-gray-700',
};

const evLine = (e) => [e.city, e.state].filter(Boolean).join(', ');

export const MissingPaymentChallengeModal = ({ open, onClose, onSubmitted }) => {
  const [mine, setMine] = useState([]);
  const [loadingMine, setLoadingMine] = useState(true);

  // event search
  const [query, setQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [events, setEvents] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);

  // manual entry
  const [manualMode, setManualMode] = useState(false);
  const [manual, setManual] = useState({
    event_name: '', date_worked: '', city: '', state: '', venue: '',
    client_name: '', start_time: '', end_time: '', break_minutes: '', onsite_contact: '',
  });

  const [reason, setReason] = useState('forgot_clock_in');
  const [explanation, setExplanation] = useState('');
  const [ack, setAck] = useState(false);
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const loadMine = () => {
    setLoadingMine(true);
    api.get('/talent/missing-payment-challenges')
      .then((c) => setMine(Array.isArray(c.data) ? c.data : []))
      .catch(() => setMine([]))
      .finally(() => setLoadingMine(false));
  };

  useEffect(() => {
    if (!open) return;
    loadMine();
    setSelected(null); setManualMode(false); setQuery(''); setDateFrom(''); setDateTo('');
    setExplanation(''); setAck(false); setDocs([]); setReason('forgot_clock_in');
  }, [open]);

  // Debounced event search
  useEffect(() => {
    if (!open || manualMode) return;
    const t = setTimeout(() => {
      setSearching(true);
      const params = new URLSearchParams();
      if (query.trim()) params.set('q', query.trim());
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      api.get(`/talent/challenge/event-search?${params.toString()}`)
        .then((r) => setEvents(r.data?.events || []))
        .catch(() => setEvents([]))
        .finally(() => setSearching(false));
    }, 350);
    return () => clearTimeout(t);
  }, [open, manualMode, query, dateFrom, dateTo]);

  const onPickFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append('file', f);
        const r = await api.post('/talent/challenge/upload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setDocs((d) => [...d, r.data]);
      } catch (err) {
        const det = err.response?.data?.detail;
        toast.error(typeof det === 'string' ? det : `Could not upload ${f.name}`);
      }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const submit = async () => {
    if (!manualMode && !selected) return toast.error('Pick the event, or choose “My event isn’t listed”.');
    if (manualMode && !manual.event_name.trim()) return toast.error('Enter the event name.');
    if (!explanation.trim()) return toast.error('Tell us what happened.');
    if (!ack) return toast.error('Please acknowledge the review timeline.');
    setSubmitting(true);
    try {
      const payload = {
        category: reason, explanation: explanation.trim(), acknowledged: ack, attachments: docs,
      };
      if (manualMode) {
        payload.manual_event = {
          ...manual,
          break_minutes: manual.break_minutes ? Number(manual.break_minutes) : null,
        };
      } else {
        payload.shift_id = selected.shift_id;
        if (selected.booking_id) payload.booking_id = selected.booking_id;
      }
      const r = await api.post('/talent/challenge/submit', payload);
      toast.success(`Submitted — case ${r.data?.case_number}. Now Under Review.`);
      onSubmitted && onSubmitted();
      loadMine();
      setSelected(null); setManualMode(false); setExplanation(''); setDocs([]); setAck(false);
      setReason('forgot_clock_in'); setQuery(''); setDateFrom(''); setDateTo('');
      setManual({ event_name: '', date_worked: '', city: '', state: '', venue: '',
        client_name: '', start_time: '', end_time: '', break_minutes: '', onsite_contact: '' });
    } catch (err) {
      const det = err.response?.data?.detail;
      toast.error(typeof det === 'string' ? det : 'Could not submit your challenge.');
    } finally {
      setSubmitting(false);
    }
  };

  const openCases = mine.filter((m) => m.under_review);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-hidden flex flex-col" data-testid="missing-payment-modal">
        <DialogHeader>
          <DialogTitle>Report a missing shift or payment</DialogTitle>
          <DialogDescription>
            Worked an event that isn’t showing up? You can request payment even if you never
            clocked in, there’s no timesheet, or you were never added to the roster.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm overflow-y-auto pr-1 flex-1">
          {/* Existing cases with payout status */}
          {!loadingMine && mine.length > 0 && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="my-cases-list">
              <p className="font-semibold text-slate-700 flex items-center gap-1 mb-1.5">
                <Clock className="w-4 h-4" /> Your cases
              </p>
              {mine.slice(0, 5).map((m) => (
                <div key={m.id} className="flex items-center justify-between gap-2 text-xs py-0.5" data-testid={`case-row-${m.id}`}>
                  <span className="truncate text-slate-700">
                    <span className="font-mono text-[10px] text-slate-400 mr-1">{m.case_number}</span>
                    {(m.event_title || 'Event')}{m.shift_date ? ` · ${m.shift_date}` : ''}
                  </span>
                  <span className={`shrink-0 px-2 py-0.5 rounded-full ${PAYOUT_TONE[m.payout_status] || 'bg-slate-200 text-slate-700'}`}
                        data-testid={`case-status-${m.id}`}>
                    {m.payout_status}
                  </span>
                </div>
              ))}
            </div>
          )}

          {!manualMode ? (
            <>
              {/* Searchable event selector */}
              {!selected ? (
                <div data-testid="event-search-panel">
                  <Label className="text-xs">Find your event</Label>
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
                    <Input
                      className="pl-8" value={query} onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search by event, venue or city (incl. other cities you traveled to)"
                      data-testid="event-search-input"
                    />
                  </div>
                  <div className="flex gap-2 mt-2">
                    <div className="flex-1 min-w-0">
                      <Label className="text-[10px] text-slate-500">From</Label>
                      <Input type="date" className="w-full" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="event-date-from" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <Label className="text-[10px] text-slate-500">To</Label>
                      <Input type="date" className="w-full" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="event-date-to" />
                    </div>
                  </div>

                  <div className="mt-2 border rounded-md divide-y max-h-56 overflow-y-auto" data-testid="event-results">
                    {searching ? (
                      <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-cyan-500" /></div>
                    ) : events.length === 0 ? (
                      <div className="py-6 text-center text-xs text-slate-400">No matching past events found.</div>
                    ) : events.map((e) => (
                      <button key={e.shift_id} onClick={() => setSelected(e)}
                              className="w-full text-left px-3 py-2 hover:bg-cyan-50 transition-colors"
                              data-testid={`event-option-${e.shift_id}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium text-slate-800 truncate">{e.event_title}</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 shrink-0">{e.relation}</span>
                        </div>
                        <div className="text-xs text-slate-500 flex flex-wrap gap-x-2">
                          {e.shift_date && <span className="flex items-center gap-0.5"><CalendarIcon className="w-3 h-3" />{e.shift_date}</span>}
                          {evLine(e) && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{evLine(e)}</span>}
                          {e.venue_name && <span>· {e.venue_name}</span>}
                          {e.client_name && <span>· {e.client_name}</span>}
                        </div>
                      </button>
                    ))}
                  </div>

                  <button onClick={() => setManualMode(true)}
                          className="mt-2 text-xs text-cyan-700 font-medium hover:underline"
                          data-testid="event-not-listed-btn">
                    My event isn’t listed →
                  </button>
                </div>
              ) : (
                <div className="rounded-md border border-cyan-200 bg-cyan-50 p-3" data-testid="selected-event">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-semibold text-slate-800">{selected.event_title}</div>
                      <div className="text-xs text-slate-500">
                        {[selected.shift_date, evLine(selected), selected.venue_name, selected.client_name].filter(Boolean).join(' · ')}
                      </div>
                      <div className="text-[10px] mt-0.5 text-slate-500">{selected.relation}{selected.has_booking ? '' : ' · no booking on file'}</div>
                    </div>
                    <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-600" data-testid="change-event-btn">
                      <ChevronLeft className="w-3 h-3 inline" />change
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            /* Manual "my event isn't listed" entry */
            <div data-testid="manual-event-panel" className="space-y-2">
              <button onClick={() => setManualMode(false)} className="text-xs text-cyan-700 flex items-center gap-1" data-testid="manual-back-btn">
                <ChevronLeft className="w-3 h-3" /> Back to search
              </button>
              <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2">
                  <Label className="text-[10px] text-slate-500">Event name *</Label>
                  <Input value={manual.event_name} onChange={(e) => setManual({ ...manual, event_name: e.target.value })} data-testid="m-event-name" />
                </div>
                <div>
                  <Label className="text-[10px] text-slate-500">Date worked</Label>
                  <Input type="date" value={manual.date_worked} onChange={(e) => setManual({ ...manual, date_worked: e.target.value })} data-testid="m-date" />
                </div>
                <div>
                  <Label className="text-[10px] text-slate-500">Client / agency</Label>
                  <Input value={manual.client_name} onChange={(e) => setManual({ ...manual, client_name: e.target.value })} data-testid="m-client" />
                </div>
                <div>
                  <Label className="text-[10px] text-slate-500">City</Label>
                  <Input value={manual.city} onChange={(e) => setManual({ ...manual, city: e.target.value })} data-testid="m-city" />
                </div>
                <div>
                  <Label className="text-[10px] text-slate-500">State</Label>
                  <Input value={manual.state} onChange={(e) => setManual({ ...manual, state: e.target.value })} data-testid="m-state" />
                </div>
                <div className="col-span-2">
                  <Label className="text-[10px] text-slate-500">Venue / location</Label>
                  <Input value={manual.venue} onChange={(e) => setManual({ ...manual, venue: e.target.value })} data-testid="m-venue" />
                </div>
                <div>
                  <Label className="text-[10px] text-slate-500">Start time</Label>
                  <Input type="time" value={manual.start_time} onChange={(e) => setManual({ ...manual, start_time: e.target.value })} data-testid="m-start" />
                </div>
                <div>
                  <Label className="text-[10px] text-slate-500">End time</Label>
                  <Input type="time" value={manual.end_time} onChange={(e) => setManual({ ...manual, end_time: e.target.value })} data-testid="m-end" />
                </div>
                <div>
                  <Label className="text-[10px] text-slate-500">Break (minutes)</Label>
                  <Input type="number" value={manual.break_minutes} onChange={(e) => setManual({ ...manual, break_minutes: e.target.value })} data-testid="m-break" />
                </div>
                <div>
                  <Label className="text-[10px] text-slate-500">Onsite contact / witness</Label>
                  <Input value={manual.onsite_contact} onChange={(e) => setManual({ ...manual, onsite_contact: e.target.value })} data-testid="m-contact" />
                </div>
              </div>
            </div>
          )}

          {/* Reason */}
          <div>
            <Label className="text-xs">What happened?</Label>
            <select className="w-full border rounded-md h-10 px-2 bg-white" value={reason}
                    onChange={(e) => setReason(e.target.value)} data-testid="missing-reason-select">
              {REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>

          {/* Explanation */}
          <div>
            <Label className="text-xs">Tell us more</Label>
            <Textarea value={explanation} onChange={(e) => setExplanation(e.target.value)} rows={3}
                      placeholder="Describe what happened, who can confirm it, the hours you worked, etc."
                      data-testid="missing-explanation-input" />
          </div>

          {/* Real file uploads */}
          <div>
            <Label className="text-xs">Supporting photos / screenshots / documents</Label>
            <input ref={fileRef} type="file" multiple className="hidden"
                   accept="image/*,application/pdf,.csv,.xlsx,.xls"
                   onChange={(e) => onPickFiles(e.target.files)} data-testid="missing-file-input" />
            <Button type="button" variant="outline" className="w-full mt-1" onClick={() => fileRef.current?.click()}
                    disabled={uploading} data-testid="missing-upload-btn">
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Upload files
            </Button>
            {docs.map((d, i) => (
              <div key={i} className="flex items-center justify-between text-xs mt-1 bg-gray-50 rounded px-2 py-1" data-testid={`doc-row-${i}`}>
                <span className="truncate flex items-center gap-1"><Paperclip className="w-3 h-3" />{d.name}</span>
                <button onClick={() => setDocs((x) => x.filter((_, j) => j !== i))} data-testid={`doc-remove-${i}`}><X className="w-3 h-3" /></button>
              </div>
            ))}
          </div>

          <label className="flex items-start gap-2 text-xs text-gray-600">
            <Checkbox checked={ack} onCheckedChange={setAck} data-testid="missing-ack-checkbox" />
            <span>I understand this will be reviewed (it may take a few business days and documentation
              may be requested), and the anticipated payout date isn’t final while under review.</span>
          </label>

          <div className="flex items-start gap-1 text-xs text-gray-500">
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            If approved, we’ll create the missing timesheet, settlement and payout for you automatically.
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="missing-cancel-btn">Cancel</Button>
          <Button className="bg-cyan-500 hover:bg-cyan-600 text-white" disabled={submitting} onClick={submit}
                  data-testid="missing-submit-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit for review'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MissingPaymentChallengeModal;
